import {
    type CalloutSettings,
    type components,
} from '@libraries/typescript/api_client';

export type ClientAutoAssignPolicy =
    components['schemas']['ClientAutoAssignPolicy'];

export const CLIENT_AUTO_ASSIGN_POLICY_LABELS: Record<
    ClientAutoAssignPolicy,
    string
> = {
    never: 'Never auto-assign',
    first_available: 'Auto-assign the first caregiver to respond',
    previously_visited:
        'Auto-assign first to respond who has visited the client',
    preferred: "Auto-assign first to respond from the client's preferred list",
};
export type AutoAssignCaregiverPolicy =
    components['schemas']['AutoAssignCaregiverPolicy'];

export function isAutoAssignAllowed(
    calloutSettings:
        | Partial<Pick<CalloutSettings, 'allow_auto_assign'>>
        | null
        | undefined,
): boolean {
    return calloutSettings?.allow_auto_assign ?? true;
}

/**
 * Whether shifts for a client should be auto-assigned by default.
 *
 * A per-client policy wins; `null` means "use the org default".
 */
export function resolveClientAutoAssignDefault({
    clientAutoAssignPolicy,
    organizationAutoAssignFirstResponderDefault,
}: {
    readonly clientAutoAssignPolicy: ClientAutoAssignPolicy | null | undefined;
    readonly organizationAutoAssignFirstResponderDefault: boolean;
}): boolean {
    if (
        clientAutoAssignPolicy !== null &&
        clientAutoAssignPolicy !== undefined
    ) {
        return clientAutoAssignPolicy !== 'never';
    }
    return organizationAutoAssignFirstResponderDefault;
}

export function resolveOrganizationAutoAssignPolicy(
    calloutSettings:
        | Pick<
              CalloutSettings,
              | 'auto_assign_first_responder_default'
              | 'auto_assign_caregiver_policy'
          >
        | null
        | undefined,
): ClientAutoAssignPolicy {
    if (!calloutSettings?.auto_assign_first_responder_default) {
        return 'never';
    }
    return calloutSettings.auto_assign_caregiver_policy ?? 'first_available';
}

export function getAutoAssignTooltip(
    policy: AutoAssignCaregiverPolicy | undefined,
): string {
    switch (policy) {
        case 'previously_visited':
            return 'Automatically assign this shift to the first caregiver who confirms and has visited this client before. Other caregivers who confirm are held for your review.';
        case 'preferred':
            return "Automatically assign this shift to the first caregiver who confirms and is on the client's preferred list. Other caregivers who confirm are held for your review.";
        default:
            return 'Automatically assign this shift to the first caregiver who confirms after reviewing care details';
    }
}
